import { reactive } from "vue";
import { useFilters } from "@/composables/filters/useFilters";

let instance = null;

export function useAccountViewState() {
  if (instance) return instance;
  const filter = useFilters();
  const state = reactive({
    filters: {
      values: [],
      searchText: "",
      showFilterDialog: false,
      key: "accountsList",
      clearSelection: "",
      statusOptions: ["draft", "active", "terminated"],
      filterGroups: [
        {
          label: "Archived",
          items: [
            { label: "No Archived", value: "no_archived" },
            { label: "No Filter", value: "no_filter" },
            { label: "Show Archived", value: "show_archived" },
          ],
        },
      ],
    },
    loading: {
      table: false,
      assignmentWizard: false,
      relieveAssignment: false,
    },
    addPayRangeModal: {
      show: false,
      message: "",
    },
    showOnwerShipDialog: {
      show: false,
      message: "",
    },
    table: {
      selectedEmployees: [],
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      search: "",
    },
    currentSelectedUser: null,
    onUserAction: null,
    lastOperation: null,
  });

  const applyFilters = (filters) => {
    const newArray = filters
      .map((item) => {
        if (item.value !== null) {
          if (typeof item === "string") {
            return item;
          } else if (
            typeof item === "object" &&
            // eslint-disable-next-line no-prototype-builtins
            item.hasOwnProperty("value")
          ) {
            return item.value;
          }
        }
      })
      .filter(Boolean);
    filter.setFilters({ key: state.filters.key, filters: newArray });
    state.filters.values = newArray;
    state.filters.showFilterDialog = false;
  };

  const removeFilters = (removedFilters) => {
    filter.setFilters({
      key: state.filters.key,
      filters: state.filters.values,
    });
    state.filters.clearSelection = removedFilters;
  };

  const getUsersProps = (data) => {
    // Create full name, fallback to email if name is empty
    const fullName = (data.user.first_name + " " + data.user.last_name).trim();
    const displayName = fullName || data.user.email;

    if (data.has_employee) {
      return {
        user: {
          id: data.user.id,
          name: displayName,
          email: data.user.email,
        },
        picture: data.avatar,
        title: data.title || "",
        employeeStatus:
          data.user.state === "operational" ? "active" : data.user.state,
        userStatus:
          data.user.state === "operational" ? "active" : data.user.state,
        accountHover: true,
      };
    } else {
      return {
        user: {
          id: data.user.id,
          name: displayName,
          email: data.user.email,
        },
        userStatus:
          data.user.state === "operational" && !data.is_suspended
            ? "has-access"
            : data.is_suspended
            ? "suspended"
            : data.user.state,
        accountHover: true,
      };
    }
  };

  const getAccountOwnerProps = (data) => {
    return {
      user: {
        id: data.account,
        name: data.account_name || "Unknown Account",
        email: "-", // Placeholder for account display (not a user email)
      },
      userStatus: "has-access",
      accountHover: false,
    };
  };

  const getUserPermissionBadgeProps = (data) => {
    return {
      permissionType: data.accessChip.permissionType,
      appDropdown: data.accessChip.appDropdown,
      IsEnabled: data.accessChip.IsEnabled,
    };
  };

  const getUserStatusBadgeProps = (data) => {
    return {
      access: data.user.state,
      status: getUserAccess(data.user.state, data.is_suspended),
    };
  };

  const getUserAccess = (state, isSuspended) => {
    if (state == "operational" && isSuspended === false) {
      return "has-access";
    } else if (isSuspended === true) {
      return "suspended";
    } else {
      return state;
    }
  };

  const getEmployeeAssigmentTagProps = {
    getTypeAssignmentTag: (positionData) => {
      if (positionData.vacant) {
        return "vacant";
      } else if (positionData.occupied) {
        return "occupied";
      } else if (positionData.blocked) {
        return "blocked";
      } else if (positionData.not_allowed) {
        return "not allowed";
      }
    },
    getBasicInfo: (item) => ({
      firstName: item.first_name,
      lastName: item.last_name,
      photoUrl: item.avatar,
      addHoverEffect: true,
    }),

    getPositionInfo: (item) => ({
      positionCode: item.primary_positions[0]?.code.split(" ")[0] || null,
      positionName: item.primary_positions[0]?.name,
      fte:
        item.primary_positions.length > 0
          ? item.primary_positions[0].allocation
          : null,
    }),

    getTypeInfo: (item) => ({
      type:
        item.current_status !== "active"
          ? "not allowed"
          : item.primary_positions?.length > 0
          ? getEmployeeAssigmentTagProps.getTypeAssignmentTag(
              item.primary_positions[0]
            )
          : "no primary assignment",
    }),
  };

  const updateFilter = (filterName, value) => {
    if (filterName in state.filters) {
      state.filters[filterName] = value;
    }
  };

  const clearFilters = () => {
    state.filters = {
      status: "all",
      department: null,
      position: null,
    };
  };

  // Métodos para loading
  const setLoading = (key, value) => {
    if (key in state.loading) {
      state.loading[key] = value;
    }
  };

  const handleAccountUserDropdownOperation = async (operation) => {
    // Handle transfer ownership action
    if (operation.value === "transfer_ownership_action") {
      state.showOnwerShipDialog.show = true;
    }
  };

  // Add method to set user action callback
  const setUserActionCallback = (callback) => {
    state.onUserAction = callback;
  };

  instance = {
    state,
    applyFilters,
    removeFilters,
    updateFilter,
    clearFilters,
    setLoading,
    getUsersProps,
    getUserStatusBadgeProps,
    getUserPermissionBadgeProps,
    handleAccountUserDropdownOperation,
    setUserActionCallback,
    getAccountOwnerProps,
  };
  return instance;
}
